/**
 * Background Jobs Store
 *
 * Zustand store for tracking background jobs (imports, evaluations, etc.)
 * from the frontend. Works with the BackgroundJobsPanel component
 * and the GET /api/jobs endpoint.
 */

import { create } from "zustand"

export type JobStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"
export type JobType = "tender_import" | "evaluation" | "comparison" | "pliego_generation" | "section_extraction" | "specs_analysis" | "template_decision_analysis" | "document_review"

export interface BackgroundJob {
	id: string
	type: JobType
	status: JobStatus
	progress: number
	currentStep: string | null
	title: string | null
	subtitle: string | null
	input?: unknown
	result: unknown
	errorMessage: string | null
	createdAt: string
	startedAt: string | null
	completedAt: string | null
}

interface BackgroundJobsState {
	jobs: BackgroundJob[]
	isMinimized: boolean

	// Actions
	setJobs: (jobs: BackgroundJob[]) => void
	addJob: (job: BackgroundJob) => void
	updateJob: (id: string, data: Partial<BackgroundJob>) => void
	removeJob: (id: string) => void
	clearCompleted: () => void
	toggleMinimized: () => void

	// Computed helpers
	hasActiveJobs: () => boolean
	activeJobCount: () => number
}

export const useBackgroundJobsStore = create<BackgroundJobsState>()((set, get) => ({
	jobs: [],
	isMinimized: false,

	setJobs: (jobs) => set({ jobs }),

	addJob: (job) =>
		set((state) => ({
			jobs: [job, ...state.jobs.filter((j) => j.id !== job.id)],
			isMinimized: false, // Auto-expand when new job added
		})),

	updateJob: (id, data) =>
		set((state) => ({
			jobs: state.jobs.map((job) =>
				job.id === id ? { ...job, ...data } : job
			),
		})),

	removeJob: (id) =>
		set((state) => ({
			jobs: state.jobs.filter((job) => job.id !== id),
		})),

	clearCompleted: () =>
		set((state) => ({
			jobs: state.jobs.filter(
				(job) => job.status !== "completed" && job.status !== "failed" && job.status !== "cancelled"
			),
		})),

	toggleMinimized: () =>
		set((state) => ({ isMinimized: !state.isMinimized })),

	hasActiveJobs: () => {
		const { jobs } = get()
		return jobs.some((job) => job.status === "pending" || job.status === "processing")
	},

	activeJobCount: () => {
		const { jobs } = get()
		return jobs.filter((job) => job.status === "pending" || job.status === "processing").length
	},
}))
